// ./src/components/generic/WeekDaySelector/WeekDaySelector.tsx
import React, { useCallback, useMemo } from "react";
import {
  Group,
  UnstyledButton,
  Text,
  Input,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

// ============================================
// Types
// ============================================

export interface WeekDaySelectorProps {
  /** Selected day numbers (ISO format: 1=Monday, 7=Sunday) */
  value: number[];

  /** Callback when selection changes */
  onChange: (value: number[]) => void;

  /** Optional label */
  label?: string;

  /** Optional description */
  description?: string;

  /** Error message */
  error?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Whether to show short day names (Mon) or long (Monday) */
  variant?: "short" | "long";

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size variant */
  size?: "xs" | "sm" | "md" | "lg";

  /** Whether to allow multiple selection (default: true) */
  multiple?: boolean;
}

// ============================================
// Constants
// ============================================

// ISO weekday numbers: 1=Monday, 7=Sunday
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

// ============================================
// Component
// ============================================

const WeekDaySelectorComponent: React.FC<WeekDaySelectorProps> = ({
  value,
  onChange,
  label,
  description,
  error,
  required = false,
  variant = "short",
  disabled = false,
  size = "md",
  multiple = true,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation(["common"]);

  // Get day label based on variant
  const getDayLabel = useCallback(
    (day: number): string => {
      const key = variant === "short" ? "weekDaysShort" : "weekDays";
      return t(`common:${key}.${day}`);
    },
    [t, variant]
  );

  // Handle day toggle
  const handleDayClick = useCallback(
    (day: number) => {
      if (disabled) return;

      if (multiple) {
        // Multiple selection mode
        const isSelected = value.includes(day);
        if (isSelected) {
          onChange(value.filter((d) => d !== day));
        } else {
          // Keep sorted order
          onChange([...value, day].sort((a, b) => a - b));
        }
      } else {
        // Single selection mode
        onChange([day]);
      }
    },
    [value, onChange, disabled, multiple]
  );

  // Calculate button size based on size prop
  const buttonSize = useMemo(() => {
    const sizes = {
      xs: { width: 32, height: 32, fontSize: "xs" as const },
      sm: { width: 36, height: 36, fontSize: "sm" as const },
      md: { width: 44, height: 44, fontSize: "sm" as const },
      lg: { width: 52, height: 52, fontSize: "md" as const },
    };
    return sizes[size];
  }, [size]);

  // Selected set for quick lookup
  const selectedSet = useMemo(() => new Set(value), [value]);

  return (
    <Input.Wrapper
      label={label}
      description={description}
      error={error}
      required={required}
      size={size}
    >
      <Group gap="xs" mt={label || description ? "xs" : 0}>
        {WEEKDAYS.map((day) => {
          const isSelected = selectedSet.has(day);

          return (
            <UnstyledButton
              key={day}
              onClick={() => handleDayClick(day)}
              disabled={disabled}
              style={{
                width: buttonSize.width,
                height: buttonSize.height,
                borderRadius: theme.radius.sm,
                border: `1px solid ${isSelected
                  ? theme.colors.blue[6]
                  : error
                    ? theme.colors.red[6]
                    : theme.colors.gray[4]
                  }`,
                backgroundColor: isSelected
                  ? theme.colors.blue[6]
                  : "transparent",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 150ms ease",
              }}
            >
              <Text
                size={buttonSize.fontSize}
                fw={isSelected ? 600 : 400}
                c={isSelected ? "white" : "dimmed"}
              >
                {getDayLabel(day)}
              </Text>
            </UnstyledButton>
          );
        })}
      </Group>
    </Input.Wrapper>
  );
};

export const WeekDaySelector = React.memo(WeekDaySelectorComponent);